import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Plus,
  PanelsTopLeft,
  FolderOpen,
  Settings,
  CreditCard,
  ChevronsLeft,
  LogOut,
  ChevronDown,
  FileText,
  Youtube,
  GraduationCap,
  PenLine,
  Coins,
  MessageCircle,
  Gift,
  Images,
  Plug,
  AudioLines,
  Film,
  GitBranch,
  Users,
} from 'lucide-react';
import { useAuthContext } from './AuthProvider';
import { getMyOrg } from '../lib/api';
import {
  fetchOrgAssetsIfStale,
  fetchCharactersIfStale,
  fetchLibraryCharactersIfStale,
  fetchProjectsIfStale,
  fetchVoiceLibraryIfStale,
  fetchSpacesIfStale,
} from '../stores';
import BuyCreditsModal from './BuyCreditsModal';
import OrganizationIcon from './OrganizationIcon';
import SupportChat from './SupportChat';
import { useTheme } from '../hooks/useTheme';
import './AppSidebar.css';

/**
 * Dual-mode navigation component:
 * - Authenticated users: Left sidebar with full navigation
 * - Unauthenticated users: Top navbar with Pricing, Enterprise, Resources
 */
export function AppSidebar({
  collapsed,
  onToggleCollapse,
  hasPaymentMethod = false,
  billingChecked = false,
  isAuthenticated = false,
  creditBalanceCents = null,
  billingMode = null,
}) {
  const location = useLocation();
  const { user, signOut, openAuthModal } = useAuthContext();
  const [showLogoutTooltip, setShowLogoutTooltip] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [organization, setOrganization] = useState(null);
  const tooltipRef = useRef(null);
  const isDarkTheme = useTheme();

  const logoSrc = isDarkTheme ? '/latted_white.png' : '/latted_black.png';
  const iconSrc = isDarkTheme ? '/latted_icon_white.png' : '/latted_icon_black.png';

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        !event.target.closest('[data-logout-tooltip]')
      ) {
        setShowLogoutTooltip(false);
      }
    };

    if (showLogoutTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogoutTooltip]);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !user?.id) {
      setOrganization(null);
      return undefined;
    }

    getMyOrg()
      .then((response) => {
        if (!cancelled) setOrganization(response?.organization || null);
      })
      .catch(() => {
        if (!cancelled) setOrganization(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleSidebarClick = (e) => {
    // Uncollapse if clicking on the sidebar or spacer area
    if (collapsed && (e.target === e.currentTarget || e.target.classList.contains('app-sidebar__spacer'))) {
      onToggleCollapse();
    }
  };

  // ============================================
  // UNAUTHENTICATED: Top Navbar
  // ============================================
  if (!isAuthenticated) {
    return (
      <header className="app-topnav">
        <div className="app-topnav__container">
          {/* Logo */}
          <Link to="/" className="app-topnav__logo-link">
            <img 
              src={logoSrc}
              alt="Latted" 
              className="app-topnav__logo" 
            />
          </Link>

          {/* Navigation Links */}
          <nav className="app-topnav__nav">
            <Link to="/pricing" className="app-topnav__link">
              Pricing
            </Link>
            <Link to="/examples" className="app-topnav__link">
              Examples
            </Link>
            <a
              href="https://cal.com/gucer/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="app-topnav__link"
            >
              Enterprise
            </a>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="app-topnav__link app-topnav__link--dropdown">
                  Resources
                  <ChevronDown size={16} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="resources-dropdown" sideOffset={12} align="center">
                  <div className="resources-dropdown__grid">
                    {/* Documentation - Full width */}
                    <DropdownMenu.Item asChild>
                      <Link to="/docs" className="resources-dropdown__panel resources-dropdown__panel--featured">
                        <div className="resources-dropdown__panel-icon resources-dropdown__panel-icon--docs">
                          <FileText size={18} />
                        </div>
                        <div className="resources-dropdown__panel-content">
                          <span className="resources-dropdown__panel-title">Documentation</span>
                          <span className="resources-dropdown__panel-desc">Complete product guide</span>
                        </div>
                      </Link>
                    </DropdownMenu.Item>

                    {/* YouTube */}
                    <DropdownMenu.Item asChild>
                      <a 
                        href="https://youtube.com/@latted" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="resources-dropdown__panel resources-dropdown__panel--youtube"
                      >
                        <div className="resources-dropdown__panel-icon resources-dropdown__panel-icon--youtube">
                          <Youtube size={18} />
                        </div>
                        <div className="resources-dropdown__panel-content">
                          <span className="resources-dropdown__panel-title">YouTube</span>
                          <span className="resources-dropdown__panel-desc">Video walkthroughs</span>
                        </div>
                      </a>
                    </DropdownMenu.Item>

                    {/* Tutorials */}
                    <DropdownMenu.Item asChild>
                      <Link to="/docs#quick-start-workflows" className="resources-dropdown__panel resources-dropdown__panel--tutorials">
                        <div className="resources-dropdown__panel-icon resources-dropdown__panel-icon--tutorials">
                          <GraduationCap size={18} />
                        </div>
                        <div className="resources-dropdown__panel-content">
                          <span className="resources-dropdown__panel-title">Tutorials</span>
                          <span className="resources-dropdown__panel-desc">Step-by-step guides</span>
                        </div>
                      </Link>
                    </DropdownMenu.Item>

                    {/* Blog - Full width */}
                    <DropdownMenu.Item asChild>
                      <Link to="/blog" className="resources-dropdown__panel resources-dropdown__panel--blog">
                        <div className="resources-dropdown__panel-icon resources-dropdown__panel-icon--blog">
                          <PenLine size={18} />
                        </div>
                        <div className="resources-dropdown__panel-content">
                          <span className="resources-dropdown__panel-title">Blog</span>
                          <span className="resources-dropdown__panel-desc">Latest news and insights</span>
                        </div>
                      </Link>
                    </DropdownMenu.Item>
                  </div>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </nav>

          {/* Right side: Auth buttons */}
          <div className="app-topnav__actions">
            <button
              type="button"
              className="app-topnav__signin-btn"
              onClick={openAuthModal}
            >
              Sign in
            </button>
            <button
              type="button"
              className="app-topnav__cta-btn"
              onClick={openAuthModal}
            >
              Get started
            </button>
          </div>
        </div>
      </header>
    );
  }

  // ============================================
  // AUTHENTICATED: Left Sidebar
  // ============================================
  return (
    <Tooltip.Provider delayDuration={200}>
      <aside 
        className={`app-sidebar ${collapsed ? 'app-sidebar--collapsed' : ''}`}
        onClick={handleSidebarClick}
      >
        {/* Header: Logo + Collapse */}
        <div className="app-sidebar__header">
          <Link to="/" className="app-sidebar__logo-link">
            <img 
              src={iconSrc}
              alt="Latted" 
              className="app-sidebar__logo" 
            />
          </Link>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className="app-sidebar__collapse-btn"
                onClick={onToggleCollapse}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <ChevronsLeft 
                  size={18}
                  style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }}
                />
              </button>
            </Tooltip.Trigger>
            {collapsed && (
              <Tooltip.Portal>
                <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                  Expand
                  <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        </div>

        {/* Navigation */}
        <nav className="app-sidebar__nav">
          <ul className="app-sidebar__nav-list">
            {[
              {
                label: 'Create',
                to: '/',
                icon: Plus,
                active: location.pathname === '/',
              },
              {
                label: 'Projects',
                to: '/projects',
                icon: PanelsTopLeft,
                active: isActive('/projects') || isActive('/project/'),
                preload: () => fetchProjectsIfStale({ userId: user?.id }),
              },
              {
                label: 'Spaces',
                to: '/spaces',
                icon: FolderOpen,
                active: isActive('/spaces'),
                preload: () => fetchSpacesIfStale({ userId: user?.id }),
              },
              {
                label: 'Videos',
                to: '/videos',
                icon: Film,
                active: isActive('/videos'),
              },
              {
                label: 'Images',
                to: '/assets',
                icon: Images,
                active: isActive('/assets'),
                preload: () => fetchOrgAssetsIfStale({ userId: user?.id }),
              },
              {
                label: 'Characters',
                to: '/characters',
                icon: Users,
                active: isActive('/characters'),
                preload: () => {
                  fetchCharactersIfStale({ userId: user?.id });
                  fetchLibraryCharactersIfStale({ userId: user?.id });
                },
              },
              {
                label: 'Voices',
                to: '/voices',
                icon: AudioLines,
                active: isActive('/voices'),
                preload: () => fetchVoiceLibraryIfStale({ userId: user?.id }),
              },
              {
                label: 'Workflows',
                to: '/workflows',
                icon: GitBranch,
                active: isActive('/workflows'),
              },
              {
                label: 'Integrations',
                to: '/integrations',
                icon: Plug,
                active: isActive('/integrations'),
              },
            ].map(({ label, to, icon: Icon, active, preload }) => (
              <li key={to}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Link
                      to={to}
                      className={`app-sidebar__nav-item ${active ? 'app-sidebar__nav-item--active' : ''}`}
                      onMouseEnter={preload}
                      onFocus={preload}
                    >
                      <Icon size={20} />
                      {!collapsed && <span>{label}</span>}
                    </Link>
                  </Tooltip.Trigger>
                  {collapsed && (
                    <Tooltip.Portal>
                      <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                        {label}
                        <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  )}
                </Tooltip.Root>
              </li>
            ))}
          </ul>
        </nav>

        {/* Spacer */}
        <div className="app-sidebar__spacer" />

        {/* Bottom section */}
        <div className="app-sidebar__bottom">
          {/* Credit balance display - for users with credits */}
          {creditBalanceCents !== null && creditBalanceCents > 0 && !collapsed && (
            <button
              type="button"
              className={`app-sidebar__credit-banner${creditBalanceCents < 500 ? ' app-sidebar__credit-banner--low' : ''}`}
              onClick={() => setShowBuyCredits(true)}
            >
              <div className="app-sidebar__payment-icon">
                <Coins size={18} />
              </div>
              <div className="app-sidebar__payment-text">
                <span className="app-sidebar__payment-title">
                  ${(creditBalanceCents / 100).toFixed(2)}
                </span>
                <span className="app-sidebar__payment-subtitle">
                  {creditBalanceCents < 500 ? 'Low balance — top up' : 'Credit balance'}
                </span>
              </div>
            </button>
          )}

          {/* Credit balance collapsed indicator */}
          {creditBalanceCents !== null && creditBalanceCents > 0 && collapsed && (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  className={`app-sidebar__payment-icon-btn${creditBalanceCents < 500 ? ' app-sidebar__credit-icon--low' : ''}`}
                  onClick={() => setShowBuyCredits(true)}
                >
                  <Coins size={20} />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                  ${(creditBalanceCents / 100).toFixed(2)} credits
                  <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          )}

          {/* Buy credits banner - for new users without any billing */}
          {billingChecked && !hasPaymentMethod && creditBalanceCents === 0 && !collapsed && (
            <button
              type="button"
              className="app-sidebar__payment-banner"
              onClick={() => setShowBuyCredits(true)}
            >
              <div className="app-sidebar__payment-icon">
                <CreditCard size={18} />
              </div>
              <div className="app-sidebar__payment-text">
                <span className="app-sidebar__payment-title">Buy credits</span>
                <span className="app-sidebar__payment-subtitle">Start generating videos</span>
              </div>
            </button>
          )}

          {/* Collapsed buy credits indicator - for new users */}
          {billingChecked && !hasPaymentMethod && creditBalanceCents === 0 && collapsed && (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  className="app-sidebar__payment-icon-btn"
                  onClick={() => setShowBuyCredits(true)}
                >
                  <CreditCard size={20} />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                  Buy credits
                  <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          )}

          {/* Legacy PAYG payment banner - only for PAYG users without billing */}
          {billingChecked && !hasPaymentMethod && billingMode === 'payg' && creditBalanceCents === null && !collapsed && (
            <Link
              to="/account#payment-methods"
              className="app-sidebar__payment-banner"
            >
              <div className="app-sidebar__payment-icon">
                <CreditCard size={18} />
              </div>
              <div className="app-sidebar__payment-text">
                <span className="app-sidebar__payment-title">
                  Payment methods
                </span>
                <span className="app-sidebar__payment-subtitle">Organization billing</span>
              </div>
            </Link>
          )}

          {/* Earn $10 referral link */}
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Link
                to="/account#referrals"
                className="app-sidebar__nav-item app-sidebar__nav-item--earn"
              >
                <Gift size={20} />
                {!collapsed && <span>Earn $10</span>}
              </Link>
            </Tooltip.Trigger>
            {collapsed && (
              <Tooltip.Portal>
                <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                  Earn $10
                  <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>

          {/* Support Chat */}
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={`app-sidebar__nav-item ${showSupportChat ? 'app-sidebar__nav-item--active' : ''}`}
                onClick={() => setShowSupportChat((prev) => !prev)}
                aria-label="Open support chat"
              >
                <MessageCircle size={20} />
                {!collapsed && <span>Support Chat</span>}
              </button>
            </Tooltip.Trigger>
            {collapsed && (
              <Tooltip.Portal>
                <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                  Support Chat
                  <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>

          {/* Settings */}
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Link
                to="/account"
                className={`app-sidebar__nav-item ${isActive('/account') ? 'app-sidebar__nav-item--active' : ''}`}
              >
                <Settings size={20} />
                {!collapsed && <span>Settings</span>}
              </Link>
            </Tooltip.Trigger>
            {collapsed && (
              <Tooltip.Portal>
                <Tooltip.Content className="app-sidebar__tooltip" side="right" sideOffset={8}>
                  Settings
                  <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>

          {/* Profile avatar with logout */}
          <Tooltip.Root
            open={collapsed ? undefined : showLogoutTooltip}
            onOpenChange={collapsed ? undefined : setShowLogoutTooltip}
          >
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className="app-sidebar__profile"
                ref={tooltipRef}
                onClick={() => {
                  if (collapsed) return;
                  setShowLogoutTooltip((prev) => !prev);
                }}
                aria-label="Profile menu"
              >
                <OrganizationIcon
                  className="app-sidebar__avatar"
                  iconUrl={organization?.iconUrl}
                  name={organization?.name || user?.email}
                />
                {!collapsed && (
                  <div className="app-sidebar__profile-info">
                    <span className="app-sidebar__profile-email">{user?.email || 'User'}</span>
                  </div>
                )}
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                data-logout-tooltip
                className={`app-sidebar__tooltip ${!collapsed ? 'app-sidebar__tooltip--logout' : ''}`}
                side="right"
                sideOffset={8}
              >
                {collapsed ? (
                  user?.email || 'Profile'
                ) : (
                  <button
                    type="button"
                    className="app-sidebar__logout-btn"
                    onClick={() => {
                      setShowLogoutTooltip(false);
                      signOut();
                    }}
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                )}
                <Tooltip.Arrow className="app-sidebar__tooltip-arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </div>
      </aside>

      <BuyCreditsModal
        isOpen={showBuyCredits}
        onClose={() => setShowBuyCredits(false)}
      />

      {showSupportChat && (
        <SupportChat onClose={() => setShowSupportChat(false)} sidebarCollapsed={collapsed} />
      )}
    </Tooltip.Provider>
  );
}

export default AppSidebar;
